import React, { useState, useEffect, useRef } from "react";
import { ShieldCheck, ArrowRight, Loader2, RefreshCw } from "lucide-react";
import { verifyOtp, requestOtp } from "../../api/client";
import { useAuth } from "../../hooks/useAuth";

const RESEND_COOLDOWN = 60; // seconds

export default function OtpVerifyForm({ email, onBack }) {
  const { login }         = useAuth();
  const [otp, setOtp]     = useState("");
  const [loading, setLoading]   = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError]   = useState("");
  const [countdown, setCountdown] = useState(RESEND_COOLDOWN);
  const inputRef = useRef(null);

  // Focus OTP input on mount
  useEffect(() => { inputRef.current?.focus(); }, []);

  // Resend cooldown timer
  useEffect(() => {
    if (countdown <= 0) return;
    const id = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [countdown]);

  async function handleVerify(e) {
    e.preventDefault();
    if (otp.trim().length < 6) {
      setError("Please enter the full OTP.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await verifyOtp(email, otp.trim());
      localStorage.setItem("gs_is_admin", res.data.is_admin ? "true" : "false");
      login(res.data.token, res.data.account_id);
      // Redirect handled by LoginPage after auth state updates
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Invalid OTP. Please try again.";
      setError(msg);
      setOtp("");
      inputRef.current?.focus();
    } finally {
      setLoading(false);
    }
  }

  async function handleResend() {
    if (countdown > 0 || resending) return;
    setResending(true);
    setError("");
    try {
      await requestOtp(email);
      setCountdown(RESEND_COOLDOWN);
      setOtp("");
      inputRef.current?.focus();
    } catch (err) {
      setError("Failed to resend OTP. Please try again.");
    } finally {
      setResending(false);
    }
  }

  // Only allow digits
  function handleOtpChange(e) {
    const val = e.target.value.replace(/\D/g, "").slice(0, 8);
    setOtp(val);
    setError("");
  }

  return (
    <form onSubmit={handleVerify} noValidate>
      <div className="auth-otp-sent">
        <span>✓</span>
        <span>OTP sent to <strong>{email}</strong>. Check your inbox.</span>
      </div>

      {error && (
        <div className="auth-banner error">
          <span>⚠</span> {error}
        </div>
      )}

      <div className="auth-field">
        <label htmlFor="auth-otp">One-time password</label>
        <input
          id="auth-otp"
          ref={inputRef}
          type="text"
          inputMode="numeric"
          className="otp-input"
          placeholder="••••••••"
          value={otp}
          onChange={handleOtpChange}
          autoComplete="one-time-code"
          maxLength={8}
        />
        <p className="auth-field-hint">Enter the 8-digit code from your email.</p>
      </div>

      <button
        type="submit"
        className="auth-btn-primary"
        disabled={loading || otp.length < 6}
      >
        {loading ? (
          <><Loader2 size={16} className="spin" /> Verifying…</>
        ) : (
          <><ShieldCheck size={16} /> Verify &amp; Sign In <ArrowRight size={15} /></>
        )}
      </button>

      <div className="auth-resend-row" style={{ marginTop: "1rem" }}>
        <button type="button" className="auth-btn-ghost" onClick={onBack}>
          ← Change email
        </button>
        <span className="auth-resend-timer">
          {countdown > 0 ? (
            `Resend in ${countdown}s`
          ) : (
            <button
              type="button"
              className="auth-btn-ghost"
              onClick={handleResend}
              disabled={resending}
              style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}
            >
              {resending ? <Loader2 size={12} className="spin" /> : <RefreshCw size={12} />}
              Resend OTP
            </button>
          )}
        </span>
      </div>
    </form>
  );
}
